import PropTypes from 'prop-types'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import ModalComponent from 'components/common/ModalComponent'
import { DATE_FILTER_TYPES, DATE_PRESETS_BY_TYPE } from 'utils/dateRangeFilters'

const selectClassName = 'p-2 pr-10 border rounded-md shadow-sm bg-white font-normal appearance-none w-full'

const SelectField = ({ label, name, value, onChange, children }) => (
  <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
    {label}
    <span className='relative'>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={selectClassName}
      >
        {children}
      </select>
      <ChevronDownIcon className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
    </span>
  </label>
)

export default function DateRangeFilterModal ({
  isOpen,
  dateRange,
  sectors = [],
  groups = [],
  maxDate,
  onClose,
  onApply
}) {
  const getPresetOptions = (dateFilterType) => DATE_PRESETS_BY_TYPE[dateFilterType] || DATE_PRESETS_BY_TYPE.custom

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title='Advanced filters'
      initialData={dateRange}
      onSave={onApply}
      maxWidth='lg'
      customFooter={({ formData }) => (
        <>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={formData.dateFilterType === 'custom' && (!formData.dateFrom || !formData.dateTo)}
            className='px-4 py-2 rounded bg-greenPrimary text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            Apply filter
          </button>
        </>
      )}
    >
      {({ formData, handleChange }) => {
        const presetOptions = getPresetOptions(formData.dateFilterType)
        const selectedPreset = presetOptions.some(option => option.value === formData.datePreset)
          ? formData.datePreset
          : presetOptions[0]?.value
        const showDateRange = formData.dateFilterType === 'custom'
        const showWeekNumber = formData.dateFilterType === 'week' && selectedPreset === 'week_number'
        const handlePeriodChange = event => {
          handleChange(event)
          const nextPreset = getPresetOptions(event.target.value)[0]?.value || 'custom'
          handleChange({ target: { name: 'datePreset', value: nextPreset } })
        }

        return (
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <SelectField
              label='Period'
              name='dateFilterType'
              value={formData.dateFilterType}
              onChange={handlePeriodChange}
            >
              {DATE_FILTER_TYPES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </SelectField>

            <SelectField
              label='Search range'
              name='datePreset'
              value={selectedPreset}
              onChange={handleChange}
            >
              {presetOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </SelectField>

            {showDateRange && (
              <>
                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  From
                  <input
                    type='date'
                    name='dateFrom'
                    value={formData.dateFrom}
                    max={maxDate}
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>

                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  To
                  <input
                    type='date'
                    name='dateTo'
                    value={formData.dateTo}
                    min={formData.dateFrom}
                    max={maxDate}
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>
              </>
            )}

            {showWeekNumber && (
              <>
                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  Week number
                  <input
                    type='number'
                    name='weekNumber'
                    value={formData.weekNumber}
                    min='1'
                    max='53'
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>

                <label className='flex flex-col gap-1 text-sm font-medium text-gray-700'>
                  Year
                  <input
                    type='number'
                    name='weekYear'
                    value={formData.weekYear}
                    min='2000'
                    max='2100'
                    onChange={handleChange}
                    className='p-2 border rounded-md shadow-sm font-normal'
                  />
                </label>
              </>
            )}

            <SelectField
              label='Sector'
              name='sectorId'
              value={formData.sectorId}
              onChange={handleChange}
            >
              <option value=''>All sectors</option>
              {sectors.map(sector => (
                <option key={sector.blockGroupId} value={sector.blockGroupId}>
                  {sector.groupName}
                </option>
              ))}
            </SelectField>

            <SelectField
              label='Group'
              name='groupId'
              value={formData.groupId}
              onChange={handleChange}
            >
              <option value=''>All groups</option>
              {groups.map(group => (
                <option key={group.deviceGroupId} value={group.deviceGroupId}>
                  {group.groupName}
                </option>
              ))}
            </SelectField>
          </div>
        )
      }}
    </ModalComponent>
  )
}

DateRangeFilterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  dateRange: PropTypes.shape({
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    dateFilterType: PropTypes.string,
    datePreset: PropTypes.string,
    weekNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weekYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  sectors: PropTypes.array,
  groups: PropTypes.array,
  maxDate: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
}

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
}
